import { apiClient, API_PRX } from '@indipia/shared';
import type { ReportRequest } from '../customer-home/reportRequest';

/** 고객이 누른 알림톡의 출처 탭 — 토큰의 shareType이 운반한다(설계서 §7.6.2, REQ-5-15). */
export type CustomerHomeEntryTab = 'PHOTO' | 'SCHEDULE' | 'REPORT';

/** 탭 요약 — unread는 직전 방문 이후 새로 공유된 건수(단위: 사진 장수 / 일정 변경 건 / 리포트 건, 에이전트가 직접 센다). */
export interface CustomerHomeTab<T> {
  hasContent: boolean;
  lastSentDtm: string | null;
  unread: number;
  /** 에이전트 원본 응답(raw) — 탭마다 모양이 달라 BE가 통일하지 않는다. */
  content: T;
}

/** 공유사진 1장 — sharedDtm은 이 사진이 고객에게 처음 열린 시각(NEW 판정 기준), bundleId는 처음 연 공유 묶음. */
export interface SharedPhoto {
  photoId: number;
  thumbUrl: string | null;
  originalUrl: string | null;
  takenAt: string | null;
  sharedDtm: string;
  bundleId: string;
}
/** 공유 묶음 1건 — 고객에게 처음 나간 발송의 제목·메시지·발송자·발송시각. 제목·메시지는 비어 있을 수 있다. */
export interface PhotoShare {
  bundleId: string;
  title: string | null;
  message: string | null;
  senderName: string | null;
  sentDtm: string;
}
export interface PhotoTabContent {
  photos?: SharedPhoto[];
  /** 공유 묶음 표시 정보 — 최신 발송 먼저. photos[].bundleId 가 가리킨다. */
  shares?: PhotoShare[];
}

/** 현장리포트 카드 1건 = 발송 스냅샷 1건(§4.1.13과 동일 모델). */
export interface ReportCard {
  crItemId: number;
  workDate: string;
  workTypeNames: string[];
  customerMemo: string | null;
  photos: { photoSeq: number; url: string }[];
  sentDtm: string;
  /** 게시된 고객 요청('✓ 요청하신 사항') — 서버는 항상 내려주나 구버전 응답을 위해 선택값으로 둔다. */
  requests?: ReportRequest[];
}
export interface ReportTabContent {
  reportCards?: ReportCard[];
}

export interface ScheduleTask {
  taskId: number;
  workTypeName: string | null;
  startDate: string;
  endDate: string;
  duration: number;
  displayOrder: number;
  /** 직전 방문 이후 공유된 일정에서 날짜가 바뀐 공종 — 공종명 옆 주황 점·간트 바 점선 외곽선으로 표시한다. 없으면 false. */
  changed?: boolean;
}
export interface ScheduleMilestone {
  milestoneName: string;
  milestoneDate: string;
  dDay: number;
}
export interface ScheduleTabContent {
  startDate?: string;
  endDate?: string;
  tasks?: ScheduleTask[];
  nextMilestone?: ScheduleMilestone | null;
}

/** GET /indipia/customer-home/me 응답 data (설계서 §7.6.3). found=false면 진입 불가(미지원·차단·만료 토큰). */
export interface CustomerHomeData {
  found: boolean;
  /** found=false 일 때 열람 불가 사유 — 판단할 수 없으면 null, found=true 면 항상 null. */
  reason?: 'EXPIRED' | 'REVOKED' | 'INVALID' | 'TEMPORARY' | null;
  siteId: number | null;
  siteName: string | null;
  siteAddress: string | null;
  customerId: number | null;
  customerName: string | null;
  entryTab: CustomerHomeEntryTab | null;
  /** 토큰 resourceRef — 에이전트별 불투명 식별자(리포트ID·사진묶음ID·일정ID). */
  entryItemRef: string | null;
  /** 직전 방문 시각(LocalDateTime) — null이면 최초 방문(새 소식 표시 없음). */
  lastVisitedDtm: string | null;
  /** 이 대시보드 진입 링크(공유 토큰)의 유효기한(LocalDateTime) — 없으면 null. */
  linkExpiresDtm: string | null;
  photo: CustomerHomeTab<PhotoTabContent> | null;
  worklog: CustomerHomeTab<ReportTabContent> | null;
  schedule: CustomerHomeTab<ScheduleTabContent> | null;
}

/**
 * 고객대시보드 진입 조회 — 외부·공유쿠키(무인증 GUEST) 경계. shareId는 URL에 싣지 않고 FlowGate가
 * 공유 쿠키에서 X-Share-Scope로 주입한다. 조회만 하며 방문시각은 갱신하지 않는다.
 */
export async function fetchCustomerHome(signal?: AbortSignal): Promise<CustomerHomeData> {
  const res = await apiClient.get(`${API_PRX.INDIPIA}/customer-home/me`, { signal });
  return res.data.data as CustomerHomeData;
}

/**
 * 발송자 미리보기 — 로그인한 사업장 회원이 고객이 볼 대시보드를 같은 모양으로 확인한다. 로그인 토큰으로 호출하며
 * 방문 기록·열람 알림·사진 열람시각 기록이 서버에서 일어나지 않는다. shareId는 발송 이력의 수신자 행 shareId.
 * historyId(선택)는 이력 화면이 연 과거 발송 건 — 서버로 그대로 넘긴다.
 */
export async function fetchCustomerHomePreview(shareId: number, signal?: AbortSignal, historyId?: number): Promise<CustomerHomeData> {
  const res = await apiClient.get(`${API_PRX.INDIPIA}/customer-home/preview`, {
    params: historyId !== undefined ? { shareId, historyId } : { shareId },
    signal,
  });
  return res.data.data as CustomerHomeData;
}

/**
 * 사진 저장(zip) 주소 — 브라우저 앵커 이동(Content-Disposition)으로 내려받는다. 카카오톡 인앱브라우저 등은 blob: 다운로드를
 * 못 받아 서버가 zip을 만들어 실제 URL로 내려준다. 공유 쿠키(share_jwt)가 자격이며 대상 현장·허용 범위는 서버가 판정한다.
 * bundleIds 생략 시 허용 범위 전체, 지정 시 그 공유 묶음의 사진만(수백 장 선택에도 URL이 길어지지 않도록 사진 ID가 아닌 묶음 ID로 지정).
 */
export function customerHomeZipUrl(bundleIds?: string[]): string {
  const base = `${import.meta.env.VITE_API_BASE || ''}/api${API_PRX.INDIPIA}/customer-home/photos/zip`;
  return bundleIds && bundleIds.length > 0 ? `${base}?bundleIds=${bundleIds.map(encodeURIComponent).join(',')}` : base;
}

/** 방문 기록 — 진입 시 판정에 쓴 직전 방문시각을 이번 진입 시각으로 갱신한다(설계서 §7.6.4, 이탈 감지·비콘 없음). */
export async function recordCustomerHomeVisit(): Promise<void> {
  await apiClient.post(`${API_PRX.INDIPIA}/customer-home/visit`);
}
